/* @flow */
import {api} from 'nutshell-core/api';
import {fetchList} from 'nutshell-core/entities/fetch-list';
import {convertToObj, convertToArray} from 'nutshell-core/filter';
import {getComparisonPeriodFilters} from 'nutshell-core/get-comparison-dates-from-range';
import {
    transformDataForReport,
    jsonToReportTableData,
    jsonToReportTableDataForTasks,
    jsonToReportTableTotalsData,
    jsonToForecastSummaryData,
} from 'nutshell-core/json-to-report-data';
import type {
    ComparedActivityReportSummary,
    ReportRowData,
    ReportsResponse,
} from 'nutshell-core/types';
import {safelyParseError} from 'nutshell-core/utils';
import type {ActionsObservable} from 'redux-observable';
import {Observable} from 'rxjs';
import {switchMap, merge, filter, map, catchError} from 'rxjs/operators';

import {
    failActivityReportSummary,
    resolveActivityReportSummary,
    type ActivityReportSummaryRequestAction,
} from './activity/activity-report-actions';
import {
    failEmailReportSummary,
    resolveEmailReportSummary,
    type EmailReportSummaryRequestAction,
} from './activity/email-report-actions';
import {mapTableDataToChannelBuckets} from './map-table-data-to-channel-buckets';
import {
    failQuotesReportSummary,
    resolveQuotesReportSummary,
    type QuotesReportSummaryRequestAction,
} from './quotes/quotes-report-actions';
import {FORECAST, ACTIVITY, EMAIL, TASKS, QUOTES, INVOICES} from './report-constants';
import type {ChartReportParams, LeadsReportKey, ReportKey} from './report-types';
import {getPostParams, type ReportPostParams} from './report-utils';
import {
    updateReportMaps,
    updateForecastSummaryStats,
    updateActivitySummaryStats,
    updateEmailSummaryStats,
    updateTasksSummaryStats,
    updateLeadsReportChartData,
    updateLeadsReportTableData,
    failLeadsReportChartData,
    updateReportLeadsListData,
    updateReportLeadsListSummaryData,
    failReportLeadsListData,
    updateQuotesSummaryStats,
    type LeadsReportDataRequestedAction,
    type LeadsReportListRequestedAction,
} from './reports-actions';
import {
    failTaskReportSummary,
    resolveTaskReportSummary,
    type TaskReportSummaryRequestAction,
} from './tasks/task-report-actions';

const REST_URL = '/rest/leads/report';
const REST_ACTIVITY_URL = '/rest/activities/report';
const REST_EMAIL_URL = '/rest/emails/report';
const REST_TASK_URL = '/rest/tasks/report';
const REST_QUOTES_URL = '/rest/quotes/report';
const REST_INVOICES_URL = '/rest/invoices/report';

const DATE_RANGE_TYPE = 'period';

export const requestReportLeadsListEpic = (action$: ActionsObservable<*>) =>
    action$.ofType('REPORT_LEADS_LIST_DATA_REQUESTED').pipe(
        switchMap((action: LeadsReportListRequestedAction) => {
            const {filters, pageNum = 1, columns, sort, reportKey} = action.payload;

            // Our pagination components aren't 0-based, but this specific
            // request expects page 0 to be the first page. Using Math.max() here
            // in case older URLs with page 0 are still in the wild
            const adjustedPageNum = Math.max(0, pageNum - 1);

            const listRequestStream = Observable.from(
                fetchList('leads', {
                    filter: filters,
                    sort,
                    page: {page: adjustedPageNum},
                    fields: columns,
                })
            );

            const leadListDataStream = listRequestStream.pipe(
                map((response) => updateReportLeadsListData(response))
            );
            const leadListSummartDataStream = listRequestStream.pipe(
                filter(() => reportKey !== FORECAST),
                map((response) => updateReportLeadsListSummaryData(response))
            );

            return leadListDataStream.pipe(
                merge(leadListSummartDataStream),
                catchError((err) => {
                    const safeError = safelyParseError(err);

                    return Observable.of(failReportLeadsListData(safeError));
                })
            );
        })
    );

export const requestLeadsReportDataEpic = (action$: ActionsObservable<*>) =>
    action$.ofType('REPORTS_LEADS_REPORT_DATA_REQUESTED').pipe(
        switchMap((action: LeadsReportDataRequestedAction) => {
            const {reportKey, reportParams, filters} = action.payload;
            const params = getPostParams(reportKey, reportParams, filters);
            const comparisonParams = getComparisonParams(reportKey, reportParams, filters);

            const requestStream = Observable.from(fetchData(params, comparisonParams));
            const currentResultStream = requestStream.pipe(
                map((res) => res[0]) // We don't display comparison data in the table for now
            );
            const reportDataStream = requestStream.pipe(map(transformDataForReport));

            const reportMapStream = currentResultStream.pipe(
                filter(
                    (currentResult) =>
                        currentResult && currentResult.reports && currentResult.reports.length
                ),
                map((currentResult) => {
                    const newReportMaps = currentResult.reports.reduce((reportAccum, report) => {
                        reportAccum[report.id] = {
                            id: report.id,
                            name: report.name,
                            key: report.key,
                            prefix: report.prefix,
                        };

                        return reportAccum;
                    }, {});

                    return updateReportMaps(newReportMaps);
                })
            );

            const forecastSummaryStatStream = currentResultStream.pipe(
                filter(() => reportKey === FORECAST),
                map((currentResult) =>
                    updateForecastSummaryStats(jsonToForecastSummaryData(currentResult))
                )
            );

            const activitySummaryStatStream = currentResultStream.pipe(
                filter(() => reportKey === ACTIVITY),
                map((currentResult) => updateActivitySummaryStats(currentResult.meta))
            );

            const emailSummaryStatStream = currentResultStream.pipe(
                filter(() => reportKey === EMAIL),
                map((currentResult) => updateEmailSummaryStats(currentResult.meta))
            );

            const tasksSummaryStatStream = currentResultStream.pipe(
                filter(() => reportKey === TASKS),
                map((currentResult) => updateTasksSummaryStats(currentResult.meta))
            );

            const quotesSummaryStatStream = currentResultStream.pipe(
                filter(() => reportKey === QUOTES || reportKey === INVOICES),
                map((currentResult) => updateQuotesSummaryStats(currentResult.meta))
            );

            const reportChartDataStream = reportDataStream.pipe(
                map((data) => data.chartData),
                filter((chartData) => Boolean(chartData)),
                map((chartData) => updateLeadsReportChartData({chartData}))
            );

            const reportTableDataStream = reportDataStream.pipe(
                map(({tableData}) => {
                    if (reportKey === 'attribution') {
                        const data = mapTableDataToChannelBuckets(tableData);

                        return updateLeadsReportTableData(data);
                    }

                    return updateLeadsReportTableData(tableData);
                })
            );

            return reportMapStream.pipe(
                merge(
                    forecastSummaryStatStream.pipe(
                        merge(
                            activitySummaryStatStream.pipe(
                                merge(
                                    emailSummaryStatStream.pipe(
                                        merge(
                                            tasksSummaryStatStream.pipe(
                                                merge(
                                                    quotesSummaryStatStream.pipe(
                                                        merge(
                                                            reportChartDataStream.pipe(
                                                                merge(reportTableDataStream)
                                                            )
                                                        )
                                                    )
                                                )
                                            )
                                        )
                                    )
                                )
                            )
                        )
                    )
                ),
                catchError((err) => Observable.of(failLeadsReportChartData(err)))
            );
        })
    );

export const requestActivityReportSummaryEpic = (action$: ActionsObservable<*>) =>
    action$.ofType('ACTIVITY_REPORT_FETCH_SUMMARY_REQUEST').pipe(
        switchMap((action: ActivityReportSummaryRequestAction) => {
            const {filters, comparisonType} = action.payload;

            let previousPeriodFilters = null;
            if (comparisonType) {
                previousPeriodFilters = getComparisonPeriodFilters(
                    DATE_RANGE_TYPE,
                    filters,
                    comparisonType
                );
                if (!previousPeriodFilters) {
                    return Observable.of(failActivityReportSummary());
                }
            }
            const excludeEmpty =
                NutClientConfig.hasActivityReportExpansionFF && !previousPeriodFilters;

            return Observable.from(
                Promise.all([
                    fetchActivityReportSummaryData(filters, excludeEmpty),
                    previousPeriodFilters
                        ? fetchActivityReportSummaryData(previousPeriodFilters, excludeEmpty)
                        : Promise.resolve(null),
                    // $FlowFixMe upgrading Flow to v0.92.1 on web
                ]).then((combinedResponses: [ReportsResponse, ?ReportsResponse]) => {
                    const data: ComparedActivityReportSummary<Array<ReportRowData>> =
                        combineTwoReports(combinedResponses, jsonToReportTableData);
                    const totals: ComparedActivityReportSummary<ReportRowData> = combineTwoReports(
                        combinedResponses,
                        jsonToReportTableTotalsData
                    );

                    return resolveActivityReportSummary({data, totals});
                    // TODO (@ianvs): handle the case of no reports
                })
            ).pipe(
                catchError((err) => {
                    return Observable.of(failActivityReportSummary(combineTwoReports(err)));
                })
            );
        })
    );

export const requestEmailReportSummaryEpic = (action$: ActionsObservable<*>) =>
    action$.ofType('EMAIL_REPORT_FETCH_SUMMARY_REQUEST').pipe(
        switchMap((action: EmailReportSummaryRequestAction) => {
            const {filters, comparisonType} = action.payload;

            let previousPeriodFilters = null;
            if (comparisonType) {
                previousPeriodFilters = getComparisonPeriodFilters(
                    DATE_RANGE_TYPE,
                    filters,
                    comparisonType
                );
                if (!previousPeriodFilters) {
                    return Observable.of(failEmailReportSummary());
                }
            }

            return Observable.from(
                Promise.all([
                    fetchEmailReportSummaryData(filters),
                    previousPeriodFilters
                        ? fetchEmailReportSummaryData(previousPeriodFilters)
                        : Promise.resolve(null),
                    // $FlowFixMe upgrading Flow to v0.92.1 on web
                ]).then((combinedResponses: [ReportsResponse, ?ReportsResponse]) => {
                    const data: ComparedActivityReportSummary<Array<ReportRowData>> =
                        combineTwoReports(combinedResponses, jsonToReportTableData);
                    const totals: ComparedActivityReportSummary<ReportRowData> = combineTwoReports(
                        combinedResponses,
                        jsonToReportTableTotalsData
                    );

                    return resolveEmailReportSummary({data, totals});
                })
            ).pipe(
                catchError((err) => {
                    return Observable.of(failEmailReportSummary(err));
                })
            );
        })
    );

export const requestTaskReportSummaryEpic = (action$: ActionsObservable<*>) =>
    action$.ofType('TASK_REPORT_FETCH_SUMMARY_REQUEST').pipe(
        switchMap((action: TaskReportSummaryRequestAction) => {
            const {filters, comparisonType} = action.payload;

            let previousPeriodFilters = null;
            if (comparisonType) {
                previousPeriodFilters = getComparisonPeriodFilters(
                    DATE_RANGE_TYPE,
                    filters,
                    comparisonType
                );
                if (!previousPeriodFilters) {
                    return Observable.of(failTaskReportSummary());
                }
            }

            return Observable.from(
                Promise.all([
                    fetchTaskReportSummaryData(filters),
                    previousPeriodFilters
                        ? fetchTaskReportSummaryData(previousPeriodFilters)
                        : Promise.resolve(null),
                    // $FlowFixMe upgrading Flow to v0.92.1 on web
                ]).then((combinedResponses: [ReportsResponse, ?ReportsResponse]) => {
                    const data: ComparedActivityReportSummary<Array<ReportRowData>> =
                        combineTwoReports(combinedResponses, jsonToReportTableDataForTasks);
                    const totals: ComparedActivityReportSummary<ReportRowData> = combineTwoReports(
                        combinedResponses,
                        jsonToReportTableTotalsData
                    );

                    return resolveTaskReportSummary({data, totals});
                })
            ).pipe(
                catchError((err) => {
                    return Observable.of(failTaskReportSummary(err));
                })
            );
        })
    );

function fetchTaskReportSummaryData(filters: Object) {
    const params = {
        reportType: 'tasks',
        groupByField: 'milestone',
        segmentByField: 'taskStatuses',
        // TODO: check if the pivotFacet is necessary
        pivotFacet: 'count',
        gap: 'w',
        filter: filters,
    };

    return api.get('tasks/report', params).then((res) => res.json());
}

export const requestQuotesReportSummaryEpic = (action$: ActionsObservable<*>) =>
    action$.ofType('QUOTES_REPORT_FETCH_SUMMARY_REQUEST').pipe(
        switchMap((action: QuotesReportSummaryRequestAction) => {
            const {filters, comparisonType} = action.payload;

            let previousPeriodFilters = null;
            if (comparisonType) {
                previousPeriodFilters = getComparisonPeriodFilters(
                    DATE_RANGE_TYPE,
                    filters,
                    comparisonType
                );
                if (!previousPeriodFilters) {
                    return Observable.of(failQuotesReportSummary());
                }
            }

            return Observable.from(
                Promise.all([
                    fetchQuotesReportSummaryData(filters),
                    previousPeriodFilters
                        ? fetchQuotesReportSummaryData(previousPeriodFilters)
                        : Promise.resolve(null),
                    // $FlowFixMe upgrading Flow to v0.92.1 on web
                ]).then((combinedResponses: [ReportsResponse, ?ReportsResponse]) => {
                    const data: ComparedActivityReportSummary<Array<ReportRowData>> =
                        combineTwoReports(combinedResponses, jsonToReportTableData);
                    const totals: ComparedActivityReportSummary<ReportRowData> = combineTwoReports(
                        combinedResponses,
                        jsonToReportTableTotalsData
                    );

                    return resolveQuotesReportSummary({data, totals});
                })
            ).pipe(
                catchError((err) => {
                    return Observable.of(failQuotesReportSummary(err));
                })
            );
        })
    );

function fetchQuotesReportSummaryData(filters: Object) {
    const params = {
        reportType: 'quotes',
        groupByField: 'quoteStatuses',
        segmentByField: 'quoteStatuses',
        // TODO: check if the pivotFacet is necessary
        pivotFacet: 'count',
        gap: 'w',
        filter: filters,
    };

    return api.get('salesdocuments/report', params).then((res) => res.json());
}

function fetchData(params: ReportPostParams, comparisonParams: ?ReportPostParams): Promise<any> {
    const reportDataRequest = comparisonParams
        ? Promise.all([fetchReportData(params), fetchReportData(comparisonParams)])
        : Promise.all([fetchReportData(params)]);

    return reportDataRequest;
}

function fetchReportData(params) {
    let url = REST_URL;
    if (params.reportType === 'activity') {
        url = REST_ACTIVITY_URL;
    } else if (params.reportType === 'email') {
        url = REST_EMAIL_URL;
    } else if (params.reportType === 'tasks') {
        url = REST_TASK_URL;
    } else if (params.reportType === 'quotes') {
        url = REST_QUOTES_URL;
    } else if (params.reportType === 'invoices') {
        url = REST_INVOICES_URL;
    }

    const ajaxParams = {
        url,
        dataType: 'json',
        data: params,
    };

    return ($.ajax(ajaxParams): any); // This is how we have to convince flow that the response is a ReportsResponse
}

function getComparisonParams(
    reportKey: LeadsReportKey | ReportKey,
    reportParams: ChartReportParams,
    filters: Array<Object>
) {
    if (reportParams.compareTo && filters) {
        const compareTo = reportParams.compareTo;
        const comparisonFilters = convertToArray(
            getComparisonPeriodFilters(reportParams.dateRangeType, convertToObj(filters), compareTo)
        );
        // TODO (@ianvs): figure out what to do with this
        // const comparisonExportLink = `${REST_URL}?${Routing.param(getPostParams(reportKey, dateRangeType, newReportParams, 'csv'))}`;

        return getPostParams(reportKey, reportParams, comparisonFilters);
    }
}

/**
 * Helper to just fetch a single report of activity summary data, using some filters
 *
 * @param  {Object} filters - Filter objects to use for the fetch
 * @return {Promise}        - Fetch promise
 */
function fetchActivityReportSummaryData(filters: Object, excludeEmpty: boolean) {
    const params = {
        reportType: 'quota', // Summary data
        groupByField: 'user',
        segmentByField: 'activityType',
        filter: filters,
        excludeEmpty: excludeEmpty ? '1' : '0',
    };

    return api.get('activities/report', params).then((res) => res.json());
}

/**
 * Helper to just fetch a single report of activity summary data, using some filters
 *
 * @param  {Object} filters - Filter objects to use for the fetch
 * @return {Promise}        - Fetch promise
 */
function fetchEmailReportSummaryData(filters: Object) {
    const params = {
        reportType: 'quota', // Summary data
        groupByField: 'user',
        segmentByField: 'emailSequenceTemplateGroups',
        filter: filters,
    };

    return api.get('emails/report', params).then((res) => res.json());
}

/**
 * Helper function to combine two reports repsonses into a single object.
 *
 * Right now, this just maps both of their data and returns an object with
 * two keys - this can be dramatically improved if we feel this is insufficient.
 * @param  {ReportsResponse[]} res     Array of reports responses, length 2
 * @param  {function} [processor]      Function to apply against each of the responses, defaults to a no-op
 * @return {Object}                    Object with two keys, one for each report
 */
function combineTwoReports(
    [currentPeriod, compareTo]: [ReportsResponse, ?ReportsResponse],
    processor?: (ReportsResponse) => any
): Object {
    const safeProcessor = processor ? processor : (x) => x;

    return {
        currentPeriod: safeProcessor(currentPeriod),
        compareTo: compareTo ? safeProcessor(compareTo) : null,
    };
}
